import { ref, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../config/firebase";
import * as FileSystem from "expo-file-system/legacy";
import * as Sentry from "@sentry/react-native";

// Upload validation constants
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_PDF_SIZE = 25 * 1024 * 1024; // 25MB
// KAN-50 (chat-revamp Lane C2, voice messages). Mirrors storage.rules'
// isAudioUpload() cap (see .planning/chat-revamp/storage-rules-c-voice.md) —
// defense-in-depth so a caller gets a clear client-side error instead of an
// opaque Storage 403.
const MAX_AUDIO_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_PDF_EXTENSIONS = ['pdf'];

function getFileExtension(fileName) {
  if (!fileName) return '';
  return fileName.split('.').pop().toLowerCase();
}

// Native uploads (FileSystem.uploadAsync) have no timeout: on a stalled
// connection the promise can stay pending forever, and Create Event's form
// stays locked behind it (Oct 21 "freezes after adding the photo"). An upload
// task can be cancelled, so a timer cancels it and rejects with a clear error.
const UPLOAD_TIMEOUT_MS = 45000;

async function uploadFileWithTimeout(url, fileUri, options, timeoutMs = UPLOAD_TIMEOUT_MS) {
  const task = FileSystem.createUploadTask(url, fileUri, options);
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      task.cancelAsync().catch(() => {});
      Sentry.captureMessage("upload_timeout", { level: "warning", extra: { timeoutMs } });
      reject(new Error("Upload timed out. Check your connection and try again."));
    }, timeoutMs);
  });
  try {
    const result = await Promise.race([task.uploadAsync(), timeout]);
    // uploadAsync resolves undefined when the task was cancelled.
    if (!result) throw new Error("Upload was cancelled");
    return result;
  } finally {
    clearTimeout(timer);
  }
}

function validateImageUri(imageUri) {
  if (!imageUri || typeof imageUri !== 'string') {
    throw new Error("Invalid image URI");
  }
}

function validatePdfUri(pdfUri) {
  if (!pdfUri || typeof pdfUri !== 'string') {
    throw new Error("Invalid PDF URI");
  }
}

// KAN-50 (chat-revamp Lane C2). Maps ChatRoom's own `chatType` enum to its
// Storage path prefix — mirrors ChatRoom.js's resolvePinnedMessagesSubscriber
// comment verbatim: "event" -> event chat, "subchat" -> sub-chat, anything
// else (the "general"/"internal" club-chat sub-types, or no chatType at all)
// -> club chat. Reusing that SAME closed-enum reading (rather than a new
// "club"/"event"/"subchat" three-way key set) so this drops in directly when
// a future ChatRoom "sendVoice" wiring passes its own chatType straight
// through, with no translation layer in between.
function resolveChatAudioPrefix(chatType) {
  if (chatType === "event") return "event-chats";
  if (chatType === "subchat") return "club-subchats";
  return "club-chats";
}

export class UploadService {
  // Gallery upload for the Admin photo handoff.
  // Callers supply the object key generated once by adminPhotoHandoff; each
  // retry uses that same key and therefore cannot create a second gallery
  // object for the same selected asset.
  static async uploadClubGalleryImagesSettled(clubId, stagedAssets) {
    const uploaded = [];
    const failed = [];
    const assets = Array.isArray(stagedAssets) ? stagedAssets : [];

    try {
      if (assets.length === 0) {
        return { data: { uploaded, failed }, error: null };
      }
      if (typeof clubId !== "string" || !clubId || clubId.includes("/") || clubId.includes("\\")) {
        throw new Error("Invalid club ID");
      }

      const { auth } = await import("../config/firebase");
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error("User not authenticated");

      const settled = await Promise.all(
        assets.map(async (asset) => {
          const assetIdentity = asset?.assetIdentity;
          const objectKey = asset?.objectKey;
          const uploadUri = asset?.uploadUri || asset?.uri;
          const canonicalPath = `club-gallery/${clubId}/${uid}`;
          const storagePath = `${canonicalPath}/${objectKey || ""}`;
          const baseRecord = { assetIdentity, objectKey, storagePath };

          try {
            if (typeof assetIdentity !== "string" || !assetIdentity) {
              throw new Error("assetIdentity is required");
            }
            if (
              typeof objectKey !== "string" ||
              !objectKey ||
              objectKey.includes("/") ||
              objectKey.includes("\\")
            ) {
              throw new Error("A safe stable objectKey is required");
            }
            if (typeof uploadUri !== "string" || !uploadUri) {
              throw new Error("A local image URI is required");
            }
            if (asset.storagePath && asset.storagePath !== storagePath) {
              throw new Error("Storage path must match the canonical uploader-bound path");
            }

            const result = await this.uploadImageViaREST(uploadUri, canonicalPath, objectKey);
            if (result.error || !result.data?.url || result.data.path !== storagePath) {
              throw result.error || new Error("Gallery upload did not return the expected path");
            }
            return { ...baseRecord, status: "uploaded", url: result.data.url, error: null };
          } catch (error) {
            return {
              ...baseRecord,
              status: "failed",
              url: null,
              error: error?.message || "Gallery upload failed",
            };
          }
        })
      );

      // Preserve original selection order within both ledgers.
      settled.forEach((record) => {
        if (record.status === "uploaded") uploaded.push(record);
        else failed.push(record);
      });
      return { data: { uploaded, failed }, error: null };
    } catch (error) {
      if (__DEV__) console.error("Settled club gallery upload setup error:", error);
      return { data: { uploaded, failed }, error };
    }
  }

  static isExactClubGalleryPath(storagePath, clubId, uid) {
    if (typeof storagePath !== "string" || typeof clubId !== "string" || typeof uid !== "string") {
      return false;
    }
    const parts = storagePath.split("/");
    return (
      parts.length === 4 &&
      parts[0] === "club-gallery" &&
      parts[1] === clubId &&
      parts[2] === uid &&
      !!parts[3] &&
      !parts[3].includes("\\")
    );
  }

  // Cleanup is deliberately path-exact.  The current canonical Storage rule
  // is create-only, so a permission-denied response is returned in `failed`
  // for a later authorized/server-side cleanup rather than loosening policy.
  static async cleanupUnsavedClubGalleryUploads({ clubId, uid, uploads, savedGalleryUrls = [] }) {
    const retained = [];
    const deleted = [];
    const failed = [];
    const savedUrls = new Set(
      (Array.isArray(savedGalleryUrls) ? savedGalleryUrls : []).filter(
        (url) => typeof url === "string" && url
      )
    );

    const candidates = (Array.isArray(uploads) ? uploads : []).filter((upload) => {
      if (!upload?.url || !upload?.storagePath) return false;
      if (savedUrls.has(upload.url)) {
        retained.push({ ...upload, cleanupStatus: "retained-saved" });
        return false;
      }
      return true;
    });

    await Promise.all(
      candidates.map(async (upload) => {
        if (!this.isExactClubGalleryPath(upload.storagePath, clubId, uid)) {
          failed.push({ ...upload, cleanupStatus: "failed", error: "Refusing non-canonical gallery path" });
          return;
        }
        try {
          const { auth } = await import("../config/firebase");
          if (auth.currentUser?.uid !== uid) throw new Error("User does not own this gallery path");
          await deleteObject(ref(storage, upload.storagePath));
          deleted.push({ ...upload, cleanupStatus: "deleted", error: null });
        } catch (error) {
          failed.push({
            ...upload,
            cleanupStatus: "failed",
            error: error?.message || "Gallery cleanup failed",
          });
        }
      })
    );

    return { data: { retained, deleted, failed }, error: null };
  }

  // An update can time out after it reached Firestore.  Re-read the saved
  // gallery first; only URLs absent from that current result are cleanup
  // candidates. `readSavedGallery` is injected to avoid a service cycle and
  // keeps the caller responsible for its existing club-read authorization.
  static async reconcileAmbiguousClubGallerySave({
    clubId,
    uid,
    uploads,
    readSavedGallery,
  }) {
    if (typeof readSavedGallery !== "function") {
      return { data: null, error: new Error("readSavedGallery is required for reconciliation") };
    }

    try {
      const readResult = await readSavedGallery(clubId);
      if (readResult?.error) throw readResult.error;
      const savedGalleryUrls = Array.isArray(readResult)
        ? readResult
        : readResult?.data?.gallery || readResult?.gallery || [];
      if (!Array.isArray(savedGalleryUrls)) {
        throw new Error("Gallery reconciliation read returned an invalid gallery");
      }
      return await this.cleanupUnsavedClubGalleryUploads({
        clubId,
        uid,
        uploads,
        savedGalleryUrls,
      });
    } catch (error) {
      // Do not delete anything if the re-read is unavailable or malformed.
      const failed = (Array.isArray(uploads) ? uploads : []).map((upload) => ({
        ...upload,
        cleanupStatus: "deferred",
        error: error?.message || "Gallery reconciliation failed",
      }));
      return { data: { retained: [], deleted: [], failed }, error: null };
    }
  }

  // Upload event image using REST API (works in React Native - same as profile pictures)
  static async uploadEventImage(imageUri, eventId) {
    try {
      validateImageUri(imageUri);
      const fileName = `event-${Date.now()}.jpg`;
      const path = `event-images/${eventId}`;

      // Use the REST API approach (same as profile pictures - works in React Native)
      const result = await this.uploadImageViaREST(imageUri, path, fileName);

      if (result.error) {
        if (__DEV__) console.error("Upload result error:", result.error);
        throw result.error;
      }

      return result.data.url;
    } catch (error) {
      if (__DEV__) console.error("=== UPLOAD EVENT IMAGE ERROR ===");
      if (__DEV__) console.error("Upload event image error:", error);
      if (__DEV__) console.error("Error details:", JSON.stringify(error, null, 2));
      throw error;
    }
  }

  // Upload profile picture using REST API (WORKING SOLUTION)
  static async uploadProfilePicture(imageUri, userId) {
    try {
      const fileName = `profile-${Date.now()}.jpg`;
      const path = `user-avatars/${userId}`;

      // Use the REST API approach
      const result = await this.uploadImageViaREST(imageUri, path, fileName);

      if (result.error) {
        if (__DEV__) console.error("Upload result error:", result.error);
        throw result.error;
      }

      return result.data.url;
    } catch (error) {
      if (__DEV__) console.error("=== UPLOAD PROFILE PICTURE ERROR ===");
      if (__DEV__) console.error("Upload profile picture error:", error);
      throw error;
    }
  }

  // Upload image using REST API (bypasses React Native compatibility issues)
  static async uploadImageViaREST(imageUri, path, fileName) {
    try {
      validateImageUri(imageUri);

      // Check file size for local files
      if (!imageUri.startsWith("data:")) {
        try {
          const fileInfo = await FileSystem.getInfoAsync(imageUri, { size: true });
          if (fileInfo.exists && fileInfo.size > MAX_IMAGE_SIZE) {
            throw new Error(`Image too large (${(fileInfo.size / 1024 / 1024).toFixed(1)}MB). Max ${MAX_IMAGE_SIZE / 1024 / 1024}MB.`);
          }
        } catch (sizeErr) {
          // If we can't check size, proceed anyway (getInfoAsync may not support size on all platforms)
          if (sizeErr.message.includes("too large")) throw sizeErr;
        }
      }

      const bucketName = storage.app.options.storageBucket;
      if (!bucketName) {
        throw new Error("Storage bucket name is not configured");
      }

      const { auth } = await import("../config/firebase");
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      const idToken = await currentUser.getIdToken();
      const filePath = `${path}/${fileName}`;
      const encodedPath = encodeURIComponent(filePath);
      const uploadUrl = `https://firebasestorage.googleapis.com/v0/b/${bucketName}/o?name=${encodedPath}&uploadType=media`;

      // Fast path: use FileSystem.uploadAsync for local file URIs.
      // This streams the file natively without any base64 encoding/decoding in JS.
      if (!imageUri.startsWith("data:")) {
        const uploadResult = await uploadFileWithTimeout(uploadUrl, imageUri, {
          httpMethod: "POST",
          headers: {
            "Content-Type": "image/jpeg",
            "Authorization": `Bearer ${idToken}`,
          },
          uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
        });

        if (uploadResult.status < 200 || uploadResult.status >= 300) {
          throw new Error(`REST API error: ${uploadResult.status} - ${uploadResult.body}`);
        }

        // Construct download URL directly from upload response — skips a separate
        // getDownloadURL() network round trip (~500-1500ms savings).
        try {
          const responseJson = JSON.parse(uploadResult.body);
          if (responseJson.downloadTokens) {
            const downloadURL = `https://firebasestorage.googleapis.com/v0/b/${bucketName}/o/${encodedPath}?alt=media&token=${responseJson.downloadTokens}`;
            return { data: { url: downloadURL, path: filePath }, error: null };
          }
        } catch {
          // Fall through to getDownloadURL if response parsing fails
        }

        const storageRef = ref(storage, filePath);
        const downloadURL = await getDownloadURL(storageRef);
        return { data: { url: downloadURL, path: filePath }, error: null };
      }

      // Fallback: handle legacy data URIs (base64 encoded)
      const base64Data = imageUri.split(",")[1];
      const binaryData = atob(base64Data);
      const bytes = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i);
      }

      const uploadPromise = new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const storageRef = ref(storage, filePath);
            getDownloadURL(storageRef)
              .then((downloadURL) => resolve({ data: { url: downloadURL, path: filePath }, error: null }))
              .catch((urlError) => reject(new Error(`Failed to get download URL: ${urlError.message}`)));
          } else {
            reject(new Error(`REST API error: ${xhr.status} - ${xhr.responseText || xhr.statusText}`));
          }
        };
        xhr.onerror = () => reject(new Error(`Network request failed: ${xhr.statusText || "Unknown error"}`));
        xhr.ontimeout = () => reject(new Error("Upload timeout - request took too long"));
        xhr.onabort = () => reject(new Error("Upload was aborted"));

        xhr.open("POST", uploadUrl, true);
        xhr.setRequestHeader("Content-Type", "image/jpeg");
        xhr.setRequestHeader("Authorization", `Bearer ${idToken}`);
        xhr.timeout = 60000;
        xhr.send(bytes);
      });

      return await uploadPromise;
    } catch (error) {
      if (__DEV__) console.error("=== UPLOAD VIA REST ERROR ===");
      if (__DEV__) console.error("Upload via REST error:", error);
      throw error;
    }
  }

  // KAN-50 (chat-revamp Lane C2, voice messages). Uploads a recorded voice
  // note to Storage — mirrors uploadImageViaREST's REST-based approach
  // (works in React Native; expo-audio's recorded file URI, like a local
  // ImagePicker URI, isn't a Blob, which uploadBytes() needs), with an m4a
  // Content-Type instead of jpeg.
  //
  // Path shape mirrors the EXISTING chat image convention EXACTLY (see
  // ClubChatScreen.js/EventChatScreen.js/SubChatScreen.js's own
  // `club-chats/${chatId}/images/${uid}` path — this is the audio sibling):
  //   {club-chats|event-chats|club-subchats}/{chatId}/audio/{uid}/voice-{Date.now()}.m4a
  // The uploader-bound {uid} segment matches storage.rules' create-only,
  // owner-scoped audio match (see
  // .planning/chat-revamp/storage-rules-c-voice.md) — unlike images, there
  // is no legacy unbound audio path to also allow: this is a net-new message
  // type with no installed-build population writing an old shape.
  //
  // Content-Type: expo-audio's RecordingPresets.HIGH_QUALITY (the preset the
  // voice recorder used; the recorder UI is currently unmounted) produces an .m4a container on BOTH
  // platforms — iOS: AVAudioRecorder with IOSOutputFormat.MPEG4AAC; Android:
  // MediaRecorder with outputFormat 'mpeg4' + audioEncoder 'aac' (also an
  // MP4/m4a container, not a raw AAC stream) — so both are MIME type
  // audio/mp4, hence the single fixed Content-Type below rather than a
  // per-platform branch.
  //
  // Returns { data: { url, path }, error } — same shape as
  // uploadImageViaREST, so callers can destructure identically.
  static async uploadChatAudio(uri, chatType, chatId, uid) {
    try {
      if (!uri || typeof uri !== "string") {
        throw new Error("Invalid audio URI");
      }
      if (!chatId || typeof chatId !== "string") {
        throw new Error("chatId is required for audio upload");
      }
      if (!uid || typeof uid !== "string") {
        throw new Error("uid is required for audio upload");
      }

      // Defense-in-depth size guard — see MAX_AUDIO_SIZE's own comment.
      try {
        const fileInfo = await FileSystem.getInfoAsync(uri, { size: true });
        if (fileInfo.exists && fileInfo.size > MAX_AUDIO_SIZE) {
          throw new Error(`Recording too large (${(fileInfo.size / 1024 / 1024).toFixed(1)}MB). Max ${MAX_AUDIO_SIZE / 1024 / 1024}MB.`);
        }
      } catch (sizeErr) {
        if (sizeErr.message?.includes("too large")) throw sizeErr;
        // If we can't check size, proceed anyway (mirrors
        // uploadImageViaREST's identical fallback — getInfoAsync may not
        // support `size` on every platform).
      }

      const bucketName = storage.app.options.storageBucket;
      if (!bucketName) {
        throw new Error("Storage bucket name is not configured");
      }

      const { auth } = await import("../config/firebase");
      const currentUser = auth.currentUser;
      if (!currentUser || currentUser.uid !== uid) {
        throw new Error("User not authenticated");
      }

      const idToken = await currentUser.getIdToken();
      const prefix = resolveChatAudioPrefix(chatType);
      const fileName = `voice-${Date.now()}.m4a`;
      const filePath = `${prefix}/${chatId}/audio/${uid}/${fileName}`;
      const encodedPath = encodeURIComponent(filePath);
      const uploadUrl = `https://firebasestorage.googleapis.com/v0/b/${bucketName}/o?name=${encodedPath}&uploadType=media`;

      const uploadResult = await uploadFileWithTimeout(uploadUrl, uri, {
        httpMethod: "POST",
        headers: {
          "Content-Type": "audio/mp4",
          "Authorization": `Bearer ${idToken}`,
        },
        uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
      });

      if (uploadResult.status < 200 || uploadResult.status >= 300) {
        throw new Error(`REST API error: ${uploadResult.status} - ${uploadResult.body}`);
      }

      // Construct the download URL directly from the upload response — same
      // round-trip savings as uploadImageViaREST.
      try {
        const responseJson = JSON.parse(uploadResult.body);
        if (responseJson.downloadTokens) {
          const downloadURL = `https://firebasestorage.googleapis.com/v0/b/${bucketName}/o/${encodedPath}?alt=media&token=${responseJson.downloadTokens}`;
          return { data: { url: downloadURL, path: filePath }, error: null };
        }
      } catch {
        // Fall through to getDownloadURL if response parsing fails
      }

      const storageRef = ref(storage, filePath);
      const downloadURL = await getDownloadURL(storageRef);
      return { data: { url: downloadURL, path: filePath }, error: null };
    } catch (error) {
      if (__DEV__) console.error("Upload chat audio error:", error);
      return { data: null, error };
    }
  }

  // Upload PDF file via REST API (similar to image upload)
  static async uploadPDFViaREST(pdfUri, path, fileName) {
    try {
      validatePdfUri(pdfUri);

      // Validate file extension
      const ext = getFileExtension(fileName);
      if (ext && !ALLOWED_PDF_EXTENSIONS.includes(ext)) {
        throw new Error(`Invalid file type: .${ext}. Only PDF files are allowed.`);
      }

      // Check file size
      try {
        const fileInfo = await FileSystem.getInfoAsync(pdfUri, { size: true });
        if (fileInfo.exists && fileInfo.size > MAX_PDF_SIZE) {
          throw new Error(`PDF too large (${(fileInfo.size / 1024 / 1024).toFixed(1)}MB). Max ${MAX_PDF_SIZE / 1024 / 1024}MB.`);
        }
      } catch (sizeErr) {
        if (sizeErr.message.includes("too large")) throw sizeErr;
      }

      // Read PDF as base64
      // Use string 'base64' directly (EncodingType may not exist in expo-file-system v19+)
      const base64Data = await FileSystem.readAsStringAsync(pdfUri, {
        encoding: 'base64',
      });

      // Get Firebase storage bucket
      const bucketName = storage.app.options.storageBucket;
      if (!bucketName) {
        throw new Error("Storage bucket name is not configured");
      }

      // Get current user's ID token for authentication
      const { auth } = await import("../config/firebase");
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      const idToken = await currentUser.getIdToken();

      // Create the REST API URL
      const restUrl = `https://firebasestorage.googleapis.com/v0/b/${bucketName}/o`;
      const filePath = `${path}/${fileName}`;
      const encodedPath = encodeURIComponent(filePath);

      // Convert base64 to binary data
      const binaryData = atob(base64Data);
      const bytes = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i);
      }

      // Use XMLHttpRequest for React Native
      const uploadPromise = new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              JSON.parse(xhr.responseText); // validate response is JSON

              // Use SDK getDownloadURL() for a properly signed URL
              const storageRef = ref(storage, filePath);
              getDownloadURL(storageRef).then((downloadURL) => {
                resolve({
                  data: { url: downloadURL, path: filePath },
                  error: null,
                });
              }).catch((urlError) => {
                if (__DEV__) console.error("getDownloadURL error:", urlError);
                reject(new Error(`Failed to get download URL: ${urlError.message}`));
              });
            } catch (parseError) {
              if (__DEV__) console.error("Error parsing response:", parseError);
              reject(new Error(`Failed to parse response: ${parseError.message}`));
            }
          } else {
            const errorText = xhr.responseText || xhr.statusText;
            if (__DEV__) console.error("REST API error:", xhr.status, errorText);
            reject(new Error(`REST API error: ${xhr.status} - ${errorText}`));
          }
        };

        xhr.onerror = () => {
          reject(new Error(`Network request failed: ${xhr.statusText || "Unknown error"}`));
        };

        xhr.ontimeout = () => {
          reject(new Error("Upload timeout - request took too long"));
        };

        try {
          const fullUrl = `${restUrl}?name=${encodedPath}&uploadType=media`;
          xhr.open("POST", fullUrl, true);
          xhr.setRequestHeader("Content-Type", "application/pdf");
          xhr.setRequestHeader("Authorization", `Bearer ${idToken}`);
          xhr.timeout = 120000; // 120 seconds for PDFs
          xhr.send(bytes);
        } catch (sendError) {
          if (__DEV__) console.error("Error sending XHR:", sendError);
          reject(sendError);
        }
      });

      return await uploadPromise;
    } catch (error) {
      if (__DEV__) console.error("=== UPLOAD PDF VIA REST ERROR ===");
      if (__DEV__) console.error("Upload PDF via REST error:", error);
      throw error;
    }
  }
}
